import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'react-hot-toast';
import { 
  ArrowLeft, Loader2, Package, User, Calendar, 
  Truck, FileText, AlertCircle, CheckCircle, 
  XCircle, RefreshCw, DollarSign, ClipboardList, Box
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DeliveryView = () => {
  const navigate = useNavigate();
  const { deliveryNumber } = useParams();
  const [delivery, setDelivery] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPostingGI, setIsPostingGI] = useState(false);
  const [customers, setCustomers] = useState([]);


  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [deliveryRes, custRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/otc/deliveries/${deliveryNumber}`),
        axios.get(`${BACKEND_URL}/api/customers`).catch(() => ({ data: [] }))
      ]);
      setDelivery(deliveryRes.data);
      setCustomers(custRes.data || []);
    } catch (error) {
      console.error('Error loading delivery:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to load delivery: ${errorMsg}`);
      navigate('/otc/deliveries');
    } finally {
      setIsLoading(false);
    }
  }, [deliveryNumber, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.customer_id === customerId);
    return customer?.customer_name || customer?.name || customerId;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'CREATED': return 'bg-orange-100 text-orange-800';
      case 'PICKED': return 'bg-yellow-100 text-yellow-800';
      case 'PACKED': return 'bg-orange-100 text-orange-800';
      case 'GOODS_ISSUED': return 'bg-green-100 text-green-800';
      case 'CANCELLED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'GOODS_ISSUED': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'CANCELLED': return <XCircle className="w-5 h-5 text-red-600" />;
      default: return <Truck className="w-5 h-5 text-orange-600" />;
    }
  };

  const handlePostGoodsIssue = async (skipWarehouseCheck = false) => {
    const pickingStatus = delivery.picking_status || 'NOT_STARTED';
    const packingStatus = delivery.packing_status || 'NOT_STARTED';
    const pickingRequired = delivery.picking_required !== false;
    const packingRequired = delivery.packing_required !== false;
    
    // Check if warehouse operations are required but not complete
    if (!skipWarehouseCheck) {
      if (pickingRequired && pickingStatus !== 'PICKED') {
        const skipPicking = window.confirm(
          `Picking Status: ${pickingStatus}\n\nPicking is not complete. Do you want to:\n\n` +
          `• Click OK to skip warehouse checks and post Goods Issue directly\n` +
          `• Click Cancel to complete picking first`
        );
        if (!skipPicking) return;
        skipWarehouseCheck = true;
      }
      
      if (packingRequired && packingStatus !== 'PACKED' && !skipWarehouseCheck) {
        const skipPacking = window.confirm(
          `Packing Status: ${packingStatus}\n\nPacking is not complete. Do you want to:\n\n` +
          `• Click OK to skip warehouse checks and post Goods Issue directly\n` +
          `• Click Cancel to complete packing first`
        );
        if (!skipPacking) return;
        skipWarehouseCheck = true;
      }
    }
    
    if (!window.confirm('Are you sure you want to post Goods Issue? This will reduce stock quantities.')) {
      return;
    }

    setIsPostingGI(true);
    try {
      await axios.post(`${BACKEND_URL}/api/otc/deliveries/${deliveryNumber}/goods-issue?skip_warehouse_check=${skipWarehouseCheck}`);
      toast.success('Goods Issue posted successfully! Stock has been updated.');
      loadData(); // Refresh data
    } catch (error) {
      console.error('Error posting goods issue:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to post goods issue: ${errorMsg}`);
    } finally {
      setIsPostingGI(false);
    }
  };

  const handleCreateBilling = () => {
    navigate(`/otc/billing/create?delivery=${deliveryNumber}`);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3 text-gray-600">Loading delivery...</span>
        </div>
      </Layout>
    );
  }

  if (!delivery) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-screen">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
          <p className="text-gray-600">Delivery not found</p>
          <Button onClick={() => navigate('/otc/deliveries')} className="mt-4">
            Back to Deliveries
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/otc/deliveries')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">Delivery {delivery.delivery_number}</h1>
                <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${getStatusColor(delivery.status)}`}>
                  {getStatusIcon(delivery.status)}
                  {delivery.status?.replace('_', ' ')}
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                Sales Order: {delivery.sales_order_number} | Ship-to: {getCustomerName(delivery.ship_to_party)}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            {/* Picking - available when CREATED and not picked */}
            {delivery.status === 'CREATED' && delivery.picking_status !== 'PICKED' && (
              <Button 
                variant="outline" 
                onClick={() => navigate(`/warehouse/pick-lists/create?delivery=${deliveryNumber}`)}
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                <ClipboardList className="w-4 h-4 mr-2" />
                Create Pick List
              </Button>
            )}
            {/* Packing - available when picked */}
            {delivery.status === 'CREATED' && (delivery.picking_status === 'PICKED' || delivery.picking_status === 'PARTIALLY_PICKED') && (
              <Button 
                variant="outline" 
                onClick={() => navigate(`/warehouse/packing?delivery=${deliveryNumber}`)}
                className="border-orange-300 text-orange-700 hover:bg-orange-50"
              >
                <Box className="w-4 h-4 mr-2" />
                Pack
              </Button>
            )}
            {/* Post GI - available when CREATED */}
            {delivery.status === 'CREATED' && (
              <Button onClick={handlePostGoodsIssue} disabled={isPostingGI} data-testid="post-gi-btn">
                {isPostingGI ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Posting...
                  </>
                ) : (
                  <>
                    <Package className="w-4 h-4 mr-2" />
                    Post Goods Issue
                  </>
                )}
              </Button>
            )}
            {delivery.status === 'GOODS_ISSUED' && (
              <Button onClick={handleCreateBilling} data-testid="create-billing-btn">
                <DollarSign className="w-4 h-4 mr-2" />
                Create Billing
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header Data */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Delivery Data
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Delivery Type</p>
                  <p className="font-medium">{delivery.delivery_type}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Sales Order</p>
                  <p className="font-mono font-medium text-orange-600 cursor-pointer hover:underline"
                     onClick={() => navigate(`/otc/sales-orders/${delivery.sales_order_number}`)}>
                    {delivery.sales_order_number}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Sales Organization</p>
                  <p className="font-medium">{delivery.sales_organization || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Shipping Point</p>
                  <p className="font-medium">{delivery.shipping_point || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Shipping Condition</p>
                  <p className="font-medium">{delivery.shipping_condition || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Route</p>
                  <p className="font-medium">{delivery.route || '-'}</p>
                </div>
              </div>
            </Card>

            {/* Warehouse Operations Status */}
            {delivery.status === 'CREATED' && (
              <Card className="p-6 border-l-4 border-l-amber-500">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-amber-600" />
                  Warehouse Operations
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  {/* Picking */}
                  <div className={`p-4 rounded-lg border-2 ${delivery.picking_status === 'PICKED' ? 'border-green-500 bg-green-50' : delivery.picking_status === 'IN_PROGRESS' ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Picking</span>
                      {delivery.picking_status === 'PICKED' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <span className="text-xs px-2 py-1 bg-gray-200 rounded">
                          {delivery.picking_status || 'Not Started'}
                        </span>
                      )}
                    </div>
                    {/* Show linked pick lists */}
                    {delivery.pick_list_numbers && delivery.pick_list_numbers.length > 0 && (
                      <div className="mt-2 text-xs text-gray-600">
                        <p className="font-medium">Pick Lists:</p>
                        {delivery.pick_list_numbers.map(pl => (
                          <span 
                            key={pl} 
                            className="inline-block mr-1 text-orange-600 cursor-pointer hover:underline"
                            onClick={() => navigate(`/warehouse/pick-lists/${pl}`)}
                          >
                            {pl}
                          </span>
                        ))}
                      </div>
                    )}
                    {delivery.picking_status !== 'PICKED' && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="mt-3 w-full"
                        onClick={() => navigate(`/warehouse/pick-lists/create?delivery=${deliveryNumber}`)}
                      >
                        Create Pick List
                      </Button>
                    )}
                  </div>
                  
                  {/* Packing */}
                  <div className={`p-4 rounded-lg border-2 ${delivery.packing_status === 'PACKED' ? 'border-green-500 bg-green-50' : delivery.packing_status === 'IN_PROGRESS' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Packing</span>
                      {delivery.packing_status === 'PACKED' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <span className="text-xs px-2 py-1 bg-gray-200 rounded">
                          {delivery.packing_status || 'Not Started'}
                        </span>
                      )}
                    </div>
                    {/* Show linked handling units */}
                    {delivery.handling_unit_numbers && delivery.handling_unit_numbers.length > 0 && (
                      <div className="mt-2 text-xs text-gray-600">
                        <p className="font-medium">Handling Units: {delivery.handling_unit_numbers.length}</p>
                      </div>
                    )}
                    {delivery.packing_status !== 'PACKED' && (delivery.picking_status === 'PICKED' || !delivery.picking_status) && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="mt-3 w-full"
                        onClick={() => navigate(`/warehouse/packing?delivery=${deliveryNumber}`)}
                      >
                        Pack Items
                      </Button>
                    )}
                  </div>
                  
                  {/* Goods Issue */}
                  <div className={`p-4 rounded-lg border-2 ${delivery.status === 'GOODS_ISSUED' ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Goods Issue</span>
                      {delivery.status === 'GOODS_ISSUED' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <span className="text-xs px-2 py-1 bg-gray-200 rounded">Pending</span>
                      )}
                    </div>
                    {delivery.status === 'CREATED' && (
                      <>
                        <Button 
                          size="sm" 
                          className="mt-3 w-full"
                          onClick={() => handlePostGoodsIssue(false)}
                          disabled={isPostingGI}
                        >
                          {isPostingGI ? 'Posting...' : 'Post GI'}
                        </Button>
                        {(delivery.picking_status !== 'PICKED' || delivery.packing_status !== 'PACKED') && (
                          <p className="text-xs text-amber-600 mt-2 text-center">
                            Warehouse ops pending - will prompt to skip
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {/* Partner Data */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <User className="w-5 h-5" />
                Partners
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Ship-to Party</p>
                  <p className="font-medium">{delivery.ship_to_party}</p>
                  <p className="text-xs text-gray-600">{getCustomerName(delivery.ship_to_party)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Sold-to Party</p>
                  <p className="font-medium">{delivery.sold_to_party}</p>
                  <p className="text-xs text-gray-600">{getCustomerName(delivery.sold_to_party)}</p>
                </div>
              </div>
            </Card>

            {/* Items */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Package className="w-5 h-5" />
                Items ({delivery.items?.length || 0})
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="delivery-items-table">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-2 px-3 text-sm font-semibold">Item</th>
                      <th className="text-left py-2 px-3 text-sm font-semibold">SO Item</th>
                      <th className="text-left py-2 px-3 text-sm font-semibold">Material</th>
                      <th className="text-left py-2 px-3 text-sm font-semibold">Description</th>
                      <th className="text-right py-2 px-3 text-sm font-semibold">Delivery Qty</th>
                      <th className="text-right py-2 px-3 text-sm font-semibold">Picked Qty</th>
                      <th className="text-left py-2 px-3 text-sm font-semibold">Plant</th>
                      <th className="text-left py-2 px-3 text-sm font-semibold">SLoc</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(delivery.items || []).map((item, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-3 font-mono">{item.item_number?.toString().padStart(4, '0')}</td>
                        <td className="py-2 px-3 font-mono">{item.sales_order_item?.toString().padStart(4, '0')}</td>
                        <td className="py-2 px-3">{item.material_id || '-'}</td>
                        <td className="py-2 px-3">{item.item_description}</td>
                        <td className="py-2 px-3 text-right font-medium">
                          {item.delivery_quantity} {item.unit_of_measure}
                        </td>
                        <td className="py-2 px-3 text-right">
                          {item.picked_quantity || 0} {item.unit_of_measure}
                        </td>
                        <td className="py-2 px-3">{item.plant || '-'}</td>
                        <td className="py-2 px-3">{item.storage_location || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Dates */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Dates
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500">Planned GI Date</p>
                  <p className="font-medium">{delivery.planned_goods_issue_date?.split('T')[0]}</p>
                </div>
                {delivery.actual_goods_issue_date && (
                  <div>
                    <p className="text-xs text-gray-500">Actual GI Date</p>
                    <p className="font-medium text-green-600">{delivery.actual_goods_issue_date?.split('T')[0]}</p>
                  </div>
                )}
                {delivery.delivery_date && (
                  <div>
                    <p className="text-xs text-gray-500">Delivery Date</p>
                    <p className="font-medium">{delivery.delivery_date?.split('T')[0]}</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Weight/Volume */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Weight & Volume</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Weight:</span>
                  <span className="font-medium">{delivery.total_weight} {delivery.weight_unit}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Volume:</span>
                  <span className="font-medium">{delivery.total_volume} {delivery.volume_unit}</span>
                </div>
              </div>
            </Card>

            {/* Status Info */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Status Flow</h3>
              <div className="space-y-2">
                {['CREATED', 'PICKED', 'PACKED', 'GOODS_ISSUED'].map((status, idx) => {
                  const isActive = delivery.status === status;
                  const isPassed = ['CREATED', 'PICKED', 'PACKED', 'GOODS_ISSUED'].indexOf(delivery.status) >= idx;
                  
                  return (
                    <div key={status} className={`flex items-center gap-2 p-2 rounded ${isActive ? 'bg-orange-100' : ''}`}>
                      <div className={`w-4 h-4 rounded-full ${isPassed ? 'bg-green-500' : 'bg-gray-300'}`} />
                      <span className={`text-sm ${isActive ? 'font-bold text-orange-800' : isPassed ? 'text-green-700' : 'text-gray-500'}`}>
                        {status.replace('_', ' ')}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Notes */}
            {delivery.delivery_note && (
              <Card className="p-6">
                <h3 className="text-lg font-semibold mb-4">Notes</h3>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{delivery.delivery_note}</p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DeliveryView;
